import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom'
import { useEffect, lazy, Suspense } from 'react'
import { AnimatePresence, motion } from 'framer-motion'
import Lenis from 'lenis'
import { gsap } from 'gsap'
import { ScrollTrigger } from 'gsap/ScrollTrigger'
import Preloader from './components/Preloader'
import RouteProgressBar from './components/RouteProgressBar'
import RouteErrorBoundary from './components/RouteErrorBoundary'
import { Toaster } from 'sonner'
import Navbar from './components/Navbar'
import CustomCursor from './components/CustomCursor'
import AmbientLines from './components/AmbientLines'
import CookieBanner from './components/CookieBanner'
// Home stays eager — it's the initial route for most visits, so lazy-loading
// it would only add an extra network round-trip before first paint instead
// of avoiding one. Every other page is only needed once actually visited.
import Home from './pages/Home'
// Each of these factories is stored so it can be called again from the idle
// prefetch below — calling import() a second time for an already-fetched
// module just returns the cached module from the browser's ES module map,
// it doesn't re-fetch.
const importWebsitesPage    = () => import('./pages/WebsitesPage')
const importStartProjectPage = () => import('./pages/StartProjectPage')
const importWebsiteTypePage  = () => import('./pages/WebsiteTypePage')
const importServicesPage     = () => import('./pages/ServicesPage')
const importProjectsPage     = () => import('./pages/ProjectsPage')
const importAboutPage        = () => import('./pages/AboutPage')
const importContactPage      = () => import('./pages/ContactPage')
const importPrivacyPage      = () => import('./pages/PrivacyPage')
const importTermsPage        = () => import('./pages/TermsPage')
const importRefundPage       = () => import('./pages/RefundPage')
const importHostingPage      = () => import('./pages/HostingPage')
const importNotFoundPage     = () => import('./pages/NotFoundPage')
const importCaseStudyPage    = () => import('./pages/CaseStudyPage')
const importWebDesignBrisbanePage = () => import('./pages/WebDesignBrisbanePage')

const WebsitesPage     = lazy(importWebsitesPage)
const StartProjectPage = lazy(importStartProjectPage)
const WebsiteTypePage  = lazy(importWebsiteTypePage)
const ServicesPage     = lazy(importServicesPage)
const ProjectsPage     = lazy(importProjectsPage)
const AboutPage        = lazy(importAboutPage)
const ContactPage      = lazy(importContactPage)
const PrivacyPage      = lazy(importPrivacyPage)
const TermsPage        = lazy(importTermsPage)
const RefundPage       = lazy(importRefundPage)
const HostingPage      = lazy(importHostingPage)
const NotFoundPage     = lazy(importNotFoundPage)
const CaseStudyPage    = lazy(importCaseStudyPage)
const WebDesignBrisbanePage = lazy(importWebDesignBrisbanePage)

// A route's Framer Motion enter/exit transition and its React.lazy() chunk
// fetch are racing each other — if the chunk is still loading when
// AnimatePresence tries to animate the page swap, the transition can stall
// with the previous page still on screen (Suspense deliberately keeps prior
// content visible rather than showing the fallback mid-transition). That
// window is normally sub-100ms on a warm cache, but widens badly on a slow
// connection, or if the tab loses focus/visibility for a moment right after
// the click (very common on mobile — a notification, an app switch) since
// browsers pause rendering work for hidden tabs. Prefetching every route's
// chunk once the browser is idle after first load means that by the time a
// visitor actually clicks a nav link, the import() below almost always
// resolves from cache instead of hitting the network at all.
function usePrefetchRoutes() {
  useEffect(() => {
    const importers = [
      importWebsitesPage, importStartProjectPage, importWebsiteTypePage,
      importServicesPage, importProjectsPage, importAboutPage,
      importContactPage, importPrivacyPage, importTermsPage,
      importRefundPage, importHostingPage, importNotFoundPage,
      importCaseStudyPage, importWebDesignBrisbanePage,
    ]
    // A prefetch failure (e.g. a stale chunk hash after a deploy — see
    // RouteErrorBoundary) isn't this hook's problem to report: the real
    // navigation attempt will hit the same failure and get handled there.
    // Left uncaught, it'd just be an unhandled promise rejection.
    const run = () => importers.forEach((fn) => fn().catch(() => {}))
    if ('requestIdleCallback' in window) {
      const id = window.requestIdleCallback(run, { timeout: 4000 })
      return () => window.cancelIdleCallback(id)
    }
    const t = setTimeout(run, 2000)
    return () => clearTimeout(t)
  }, [])
}

function ScrollReset() {
  const { pathname } = useLocation()
  useEffect(() => {
    document.documentElement.scrollTop = 0
    document.body.scrollTop = 0
  }, [pathname])
  return null
}

// Suspense fallback for lazy-loaded routes — previously `null`, which on a
// slow connection meant a real gap between the route-change progress bar
// finishing (a fixed 700ms, unrelated to whether the chunk actually finished
// loading — see RouteProgressBar) and the page actually mounting: nothing
// visible in between, on this site's black background, looks identical to
// the page being broken.
function RouteLoadingFallback() {
  return (
    <div style={{
      position: 'fixed', inset: 0,
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      pointerEvents: 'none',
    }}>
      <div style={{
        width: 28, height: 28, borderRadius: '50%',
        border: '2px solid rgba(6,182,212,0.15)',
        borderTopColor: '#22d3ee',
        animation: 'route-spin 0.7s linear infinite',
      }} />
      <style>{`@keyframes route-spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  )
}

const PAGE_VARIANTS = {
  initial: { opacity: 0 },
  animate: { opacity: 1, transition: { duration: 0.38, ease: 'easeOut' } },
  exit:    { opacity: 0, transition: { duration: 0.22, ease: 'easeIn' } },
}

function AnimatedRoutes() {
  const location = useLocation()
  return (
    <AnimatePresence mode="wait" initial={false}>
      <motion.div
        key={location.pathname}
        variants={PAGE_VARIANTS}
        initial="initial"
        animate="animate"
        exit="exit"
      >
        <RouteErrorBoundary key={location.pathname}>
          <Suspense fallback={<RouteLoadingFallback />}>
            <Routes location={location}>
              <Route path="/"               element={<Home />} />
              <Route path="/websites"       element={<WebsitesPage />} />
              <Route path="/start-a-project" element={<StartProjectPage />} />
              <Route path="/websites/:slug" element={<WebsiteTypePage />} />
              <Route path="/services"       element={<ServicesPage />} />
              <Route path="/hosting"        element={<HostingPage />} />
              <Route path="/projects"       element={<ProjectsPage />} />
              <Route path="/projects/:slug" element={<CaseStudyPage />} />
              <Route path="/web-design-brisbane" element={<WebDesignBrisbanePage />} />
              <Route path="/about"          element={<AboutPage />} />
              <Route path="/contact"        element={<ContactPage />} />
              <Route path="/privacy"        element={<PrivacyPage />} />
              <Route path="/terms"          element={<TermsPage />} />
              <Route path="/refund"         element={<RefundPage />} />
              <Route path="/nova/*"         element={<Navigate to="/" replace />} />
              <Route path="/store"          element={<Navigate to="/" replace />} />
              <Route path="/subscribe"      element={<Navigate to="/" replace />} />
              <Route path="*"               element={<NotFoundPage />} />
            </Routes>
          </Suspense>
        </RouteErrorBoundary>
      </motion.div>
    </AnimatePresence>
  )
}

gsap.registerPlugin(ScrollTrigger)

function SmoothScroll() {
  useEffect(() => {
    const lenis = new Lenis({ lerp: 0.08, smoothWheel: true })
    const onScroll = () => ScrollTrigger.update()
    lenis.on('scroll', onScroll)

    const rafFn = (time) => lenis.raf(time * 1000)
    gsap.ticker.add(rafFn)
    gsap.ticker.lagSmoothing(0)

    // Mobile Safari resizes the viewport as its address bar shows/hides
    // mid-scroll, which throws off pinned/scrubbed ScrollTrigger sections
    // (Projects' pinned story, Hero's scroll-exit) — they'd visibly jump or
    // smear because their start/end math was computed against a viewport
    // height that just changed underneath them. normalizeScroll is GSAP's
    // own fix for exactly this, so it's mobile-only — desktop doesn't need it.
    const isCoarsePointer = window.matchMedia('(pointer: coarse)').matches
    const normalized = isCoarsePointer ? ScrollTrigger.normalizeScroll(true) : null

    // Let layout settle then recalculate all trigger positions
    const t = setTimeout(() => ScrollTrigger.refresh(), 150)

    return () => {
      clearTimeout(t)
      lenis.off('scroll', onScroll)
      gsap.ticker.remove(rafFn)
      lenis.destroy()
      normalized?.kill()
    }
  }, [])
  return null
}

function App() {
  usePrefetchRoutes()
  return (
    <BrowserRouter>
      <div className="noise-overlay">
        <Preloader />
        <RouteProgressBar />
        <SmoothScroll />
        <AmbientLines />
        <CustomCursor />
        <Navbar />
        <CookieBanner />
        <ScrollReset />
        <AnimatedRoutes />
        <Toaster
          position="bottom-right"
          toastOptions={{
            style: {
              background: 'rgba(10,10,20,0.95)',
              border: '1px solid rgba(6,182,212,0.2)',
              color: '#fff',
              fontFamily: 'Geist Variable, sans-serif',
              fontSize: 13,
              backdropFilter: 'blur(20px)',
              borderRadius: 12,
            },
          }}
        />
      </div>
    </BrowserRouter>
  )
}

export default App
